import React, { useState } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Badge,
  Divider,
  Input,
  InputGroup,
  InputLeftElement,
  Icon,
  SimpleGrid,
  Card,
  CardHeader,
  CardBody,
  List,
  ListItem,
  ListIcon,
} from '@chakra-ui/react';
import { SearchIcon, CheckCircleIcon, WarningIcon, InfoIcon } from '@chakra-ui/icons';

const codeData = {
  'T1015': {
    description: 'Clinic visit/encounter, all-inclusive',
    federal: {
      source: 'CMS IOM Pub. 100-04, Chapter 9',
      summary: 'Core FQHC encounter code. Must be billed with a qualifying visit code.',
      rules: [
        'Requires a face-to-face encounter with a core practitioner.',
        'Must be billed with a specific payment code (G0466-G0470) for Medicare PPS.',
        'Incident-to services are included in the all-inclusive rate.'
      ]
    },
    mac: {
      source: 'Noridian MAC FQHC Billing Guide',
      summary: 'Specific modifier requirements for telehealth and preventive services.',
      rules: [
        'Append modifier 95 for telehealth services.',
        'Do not bill T1015 for group therapy sessions (use specific G-codes).'
      ]
    },
    state: {
      source: 'Medi-Cal Provider Manual: Rural Health Clinics (RHCs) and FQHCs',
      summary: 'Strict revenue code and modifier pairings based on visit type.',
      rules: [
        'Medical Visit: Bill with Revenue Code 0521.',
        'Mental Health Visit: Bill with Revenue Code 0900 and appropriate provider modifier (e.g., AH, AJ).',
        'Managed Care Wrap: Bill with modifier SE to indicate a wrap-around payment request.'
      ]
    }
  },
  '0900': {
    description: 'Behavioral Health Treatments/Services',
    federal: {
      source: 'CMS IOM Pub. 100-04, Chapter 9',
      summary: 'General behavioral health revenue code.',
      rules: [
        'Allowed for clinical psychologist and clinical social worker encounters.',
        'May be billed on the same day as a medical visit if it is a separate, distinct encounter.'
      ]
    },
    mac: {
      source: 'Noridian MAC FQHC Billing Guide',
      summary: 'Same-day billing restrictions.',
      rules: [
        'If billed on the same day as a medical visit, requires modifier 59 on the medical service to indicate a distinct procedural service.'
      ]
    },
    state: {
      source: 'Medi-Cal Provider Manual: Psychological Services',
      summary: 'Requires specific HCPCS and modifiers for PPS reimbursement.',
      rules: [
        'Must be billed with T1015 for PPS reimbursement.',
        'Requires provider-specific modifiers: AH (Clinical Psychologist), AJ (Clinical Social Worker), or HN (Bachelors Degree Level).',
        'Maximum of one mental health encounter per patient per day, unless the patient suffers an illness or injury requiring additional diagnosis or treatment.'
      ]
    }
  }
};

const FqDemoCodeIntelligence = () => {
  const [searchTerm, setSearchTerm] = useState('T1015');
  const [activeCode, setActiveCode] = useState('T1015');

  const handleSearch = (e) => {
    if (e.key === 'Enter') {
      const term = searchTerm.toUpperCase();
      if (codeData[term]) {
        setActiveCode(term);
      }
    }
  };

  const data = codeData[activeCode];

  return (
    <Container maxW="container.xl" py={8}>
      <VStack align="stretch" spacing={6}>
        <Box>
          <Heading size="lg" color="brand.700" mb={2}>Code Intelligence Explorer</Heading>
          <Text color="gray.600">
            Demonstrating Revedy's deep regulatory knowledge across Federal, MAC, and State layers.
          </Text>
        </Box>

        <Card variant="outline" bg="white">
          <CardBody>
            <InputGroup size="lg">
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.400" />
              </InputLeftElement>
              <Input 
                placeholder="Search HCPCS or Revenue Code (e.g., T1015, 0900)" 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={handleSearch}
              />
            </InputGroup>
            {!codeData[searchTerm.toUpperCase()] && searchTerm.length > 3 && (
              <Text color="red.500" mt={2} fontSize="sm">Code not found in demo database. Try T1015 or 0900.</Text>
            )}
          </CardBody>
        </Card>

        {data && (
          <Box>
            <HStack mb={4}>
              <Heading size="md">{activeCode}</Heading>
              <Badge colorScheme="blue" fontSize="md">{data.description}</Badge>
            </HStack>

            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
              {/* Federal */}
              <Card variant="outline" borderColor="blue.200">
                <CardHeader bg="blue.50" pb={2}>
                  <HStack>
                    <Icon as={InfoIcon} color="blue.500" />
                    <Heading size="sm" color="blue.700">Federal (CMS)</Heading>
                  </HStack>
                  <Text fontSize="xs" color="gray.500" mt={1}>{data.federal.source}</Text>
                </CardHeader>
                <CardBody>
                  <Text fontWeight="bold" fontSize="sm" mb={3}>{data.federal.summary}</Text>
                  <List spacing={2}>
                    {data.federal.rules.map((rule, idx) => (
                      <ListItem key={idx} fontSize="sm">
                        <ListIcon as={CheckCircleIcon} color="green.500" />
                        {rule}
                      </ListItem>
                    ))}
                  </List>
                </CardBody>
              </Card>

              {/* MAC */}
              <Card variant="outline" borderColor="purple.200">
                <CardHeader bg="purple.50" pb={2}>
                  <HStack>
                    <Icon as={WarningIcon} color="purple.500" />
                    <Heading size="sm" color="purple.700">MAC (Noridian)</Heading>
                  </HStack>
                  <Text fontSize="xs" color="gray.500" mt={1}>{data.mac.source}</Text>
                </CardHeader>
                <CardBody>
                  <Text fontWeight="bold" fontSize="sm" mb={3}>{data.mac.summary}</Text>
                  <List spacing={2}>
                    {data.mac.rules.map((rule, idx) => (
                      <ListItem key={idx} fontSize="sm">
                        <ListIcon as={CheckCircleIcon} color="green.500" />
                        {rule}
                      </ListItem>
                    ))}
                  </List>
                </CardBody>
              </Card>

              {/* State */}
              <Card variant="outline" borderColor="green.200">
                <CardHeader bg="green.50" pb={2}>
                  <HStack>
                    <Icon as={CheckCircleIcon} color="green.500" />
                    <Heading size="sm" color="green.700">State (Medi-Cal)</Heading>
                  </HStack>
                  <Text fontSize="xs" color="gray.500" mt={1}>{data.state.source}</Text>
                </CardHeader>
                <CardBody>
                  <Text fontWeight="bold" fontSize="sm" mb={3}>{data.state.summary}</Text>
                  <List spacing={2}>
                    {data.state.rules.map((rule, idx) => (
                      <ListItem key={idx} fontSize="sm">
                        <ListIcon as={CheckCircleIcon} color="green.500" />
                        {rule}
                      </ListItem>
                    ))}
                  </List>
                </CardBody>
              </Card>
            </SimpleGrid>
          </Box>
        )}
      </VStack>
    </Container>
  );
};

export default FqDemoCodeIntelligence;
